import {
  OrderFeature,
  PROPERTY_GRID_SUBJECT_PROPERTY,
  PropertyDataWithOptionalReadonly,
  expected,
} from '@aloft/toolkit-shared';
import { Block, Flex, Grid, Icon, theme } from '@aloft/toolkit-sui';

import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router';

import { useOrchestrateAutofillPropertyAPI } from '../../hooks/api/toolkitComponents.ts';
import { logger } from '../../logging/logging.ts';
import { shallowEqual } from 'react-redux';
import {
  selectHasArvOption,
  selectOrderFeatures,
} from '../../redux/features/selectors/orderSelectors.ts';
import { selectPropertyDataById } from '../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import {
  selectAivArvActiveTab,
  selectFeatureGridExpandedPropertyId,
} from '../../redux/features/selectors/uiSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { CompPropertyDataState, SubjectPropertyDataState } from '../../redux/types.ts';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary.tsx';
import { LoadingScreen } from '../LoadingScreen/LoadingScreen.tsx';
import { HeaderPropertyGridSection } from './columnSections/HeaderPropertyGridSection.tsx';
import { PropertyGridColumnSubHeader } from './columnSections/PropertyGridColumnSubHeader.tsx';
import {
  PropertyGridColumnDefinition,
  PropertyGridColumnSectionComponent,
  PropertyGridRowFieldType,
  PropertyGridSectionProps,
  PropertyGridType,
} from './types.ts';

const log = logger();

type ColumnErrorBoundaryFallbackProps = {
  error: Error;
  errorString: string;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ColumnErrorBoundaryFallback = ({ error, errorString }: ColumnErrorBoundaryFallbackProps) => (
  <Flex
    flexDirection="column"
    width="100%"
    maxWidth="350px"
    height="100%"
    alignItems="center"
    justifyContent="center"
    gap="8px"
    textAlign="center"
  >
    <Icon icon="error-warning" color="alertErrorLight" size="xxl" />
    <Block __dangerDoNotUseSx={{ fontWeight: 'bold' }}>Error loading column</Block>
    <Block width="100%">{error.message}</Block>
  </Flex>
);

type PropertyGridColumnProps = Pick<PropertyGridColumnDefinition, 'propertyId' | 'type'> & {
  columnSections: PropertyGridColumnSectionComponent[];
  fieldTypes: PropertyGridRowFieldType[];
  gridType: PropertyGridType;
  isDataSourceDrawerOpen: boolean;
  columnIndex: number;
} & Pick<PropertyGridSectionProps, 'control' | 'getValues' | 'setValue'>;

export const PropertyGridColumn = ({
  columnSections,
  fieldTypes,
  gridType,
  isDataSourceDrawerOpen,
  propertyId,
  type,
  columnIndex,
  control,
  getValues,
  setValue,
}: PropertyGridColumnProps) => {
  const orderId = expected(useParams().orderId);

  const orderFeatures = useAppSelector(selectOrderFeatures, shallowEqual);
  const featureGridExpandedPropertyId = useAppSelector(selectFeatureGridExpandedPropertyId);
  const hasArvOption = useAppSelector(selectHasArvOption);
  const activeTab = useAppSelector(selectAivArvActiveTab);

  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;

  const isPropertyColumn = type === 'property';

  const propertyDataState = useAppSelector((state) => selectPropertyDataById(state, propertyId));

  const { data: autofillData, isLoading: autofillIsLoading } =
    useOrchestrateAutofillPropertyAPI<PropertyDataWithOptionalReadonly>(
      {
        pathParams: { orderID: orderId, propertyId },
        // @ts-expect-error - Type mismatch because ToolkitContext['fetcherOptions'] is of type Record<string, string>
        queryParams: {
          includeBackupListingData: true,
          sources: [type],
        },
      },
      {
        enabled: !isPropertyColumn,
      },
    );

  const formFieldName = `columns.${columnIndex}.propertyData` as const;

  useEffect(() => {
    if (!isPropertyColumn && autofillData) {
      setValue(formFieldName, autofillData);
    }
  }, [autofillData, formFieldName, isPropertyColumn, setValue]);

  useEffect(() => {
    if (propertyDataState && isPropertyColumn) {
      if (propertyDataState.id === PROPERTY_GRID_SUBJECT_PROPERTY) {
        setValue(formFieldName, (propertyDataState as SubjectPropertyDataState).subjectData);
      } else {
        setValue(formFieldName, (propertyDataState as CompPropertyDataState).compData);
      }
    }
  }, [propertyDataState, formFieldName, isPropertyColumn, setValue]);

  const isLoading = isPropertyColumn ? !propertyDataState : autofillIsLoading;

  const propertyIsExpanded = featureGridExpandedPropertyId === propertyId;
  const columnIsVisible = isPropertyColumn || propertyIsExpanded;

  const columnIsSticky =
    isPropertyColumn &&
    (isDataSourceDrawerOpen ? propertyIsExpanded : propertyId === PROPERTY_GRID_SUBJECT_PROPERTY);

  const columnHasMarginLeft = isPropertyColumn && propertyId !== PROPERTY_GRID_SUBJECT_PROPERTY;

  // Memoize fieldTypes to ensure referential stability for children (PropertyGridRow),
  // as PropertyGrid creates a new array on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableFieldTypes = useMemo(() => fieldTypes, [fieldTypes.join(',')]);

  const gridSectionProps: PropertyGridSectionProps = {
    fieldTypes: stableFieldTypes,
    columnIndex,
    control,
    getValues,
    setValue,
  };

  const sortedOrderFeatures: OrderFeature[] = orderFeatures
    .filter((feature) => feature !== 'manualCost')
    .sort((a, b) => a.localeCompare(b));
  if (orderFeatures.includes('manualCost')) sortedOrderFeatures.push('manualCost');

  let nbOfFieldColumns = fieldTypes.length;
  const isFeatureGrid = gridType === 'FeatureGrid';

  if (isSubject && isPropertyColumn && isFeatureGrid && hasArvOption) {
    const showAivComps = activeTab === 'all' || activeTab === 'aiv';
    const showArvComps = activeTab === 'all' || activeTab === 'arv';

    nbOfFieldColumns = 0;
    if (fieldTypes.includes('label')) nbOfFieldColumns += 1;

    if (showAivComps && fieldTypes.includes('editableValue')) {
      nbOfFieldColumns += 1;
    }
    if (showArvComps) {
      if (showAivComps) {
        if (fieldTypes.includes('editableArvValue')) {
          nbOfFieldColumns += 1;
        }
      } else if (
        fieldTypes.includes('editableArvValue') ||
        fieldTypes.includes('editableValue')
      ) {
        nbOfFieldColumns += 1;
      }
    }
  } else if (isSubject && isPropertyColumn && gridType === 'AdjustmentsGrid' && hasArvOption) {
    const showAivComps = activeTab === 'all' || activeTab === 'aiv';
    const showArvComps = activeTab === 'all' || activeTab === 'arv';

    nbOfFieldColumns = 0;
    if (fieldTypes.includes('label')) nbOfFieldColumns += 1;

    if (showAivComps && fieldTypes.includes('readOnlyValue')) {
      nbOfFieldColumns += 1;
    }
    if (showArvComps && fieldTypes.includes('readOnlyArvValue')) {
      nbOfFieldColumns += 1;
    }
  } else if (
    isPropertyColumn &&
    isFeatureGrid &&
    hasArvOption &&
    !isSubject &&
    fieldTypes.includes('editableArvValue')
  ) {
    nbOfFieldColumns -= 1;
  }

  if (!isSubject && isPropertyColumn && gridType === 'AdjustmentsGrid') {
    nbOfFieldColumns = 2;
  }

  if (!isPropertyColumn) nbOfFieldColumns = 1;

  return (
    <ErrorBoundary
      Fallback={ColumnErrorBoundaryFallback}
      onError={() => log.fatal(`Failed to display Property Grid Column ${propertyId} - ${type}`)}
    >
      <Flex
        zIndex={columnIsSticky ? theme.zIndices.sticky : theme.zIndices.base}
        marginLeft={columnHasMarginLeft ? '12px' : '0'}
        backgroundColor="surface1"
        flexDirection="column"
        height="100%"
        __dangerDoNotUseSx={{
          opacity: isDataSourceDrawerOpen && !propertyIsExpanded ? 0.5 : 1,
          display: columnIsVisible ? 'flex' : 'none',
          border: columnIsVisible ? `1px solid ${theme.colors.strokeDefault}` : 'none',
          borderTop: 'none',
        }}
        {...(columnIsSticky ? { position: 'sticky', left: 0 } : {})}
      >
        <HeaderPropertyGridSection columnIndex={columnIndex} gridType={gridType} control={control} getValues={getValues} setValue={setValue} />
        {isLoading ? (
          <Block height="100%" width="100%" position="relative">
            <LoadingScreen />
          </Block>
        ) : (
          <Grid
            backgroundColor="strokeDefault"
            gap="1px"
            gridTemplateColumns={`repeat(${nbOfFieldColumns}, 1fr)`}
          >
            <PropertyGridColumnSubHeader gridType={gridType} columnIndex={columnIndex} control={control} getValues={getValues} setValue={setValue} />
            {columnSections.map((Section) => (
              <Section
                key={`${propertyId}-${type}-${Section.name}-section`}
                {...gridSectionProps}
              />
            ))}
          </Grid>
        )}
      </Flex>
    </ErrorBoundary>
  );
};
